import React from 'react';
import { HighlightGroup } from 'components/expanded-highlights-page/types';
import { createSelector } from 'reselect';
import {
  areaHighlightsIdsSelector,
  impactfulTradeoffsSelector,
  insightsByIdsSelector,
  makeInsightsByTypeSelector,
  tradeoffIdsSelector,
} from 'store/state/domainData/selectors';
import { IInsight, InsightDescriptor, InsightType } from 'utils/entities';
import ExpandedHighlightsPage from 'components/expanded-highlights-page';
import { ExpandCollection } from 'store/state/insightsContext';
import { activeCollectionSelector, activeInsightIdSelector } from 'store/state/insightsContext/selectors';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { pricesIdsSelector } from 'store/state/selectors/insights/prices';
import { transportationIdsSelector } from 'store/state/selectors/insights/transportation';
import { planningIdsSelector } from 'store/state/selectors/insights/construction';
import { nuisancesIdsSelector } from 'store/state/selectors/insights/nuisances';
import { quietnessValueSelector } from 'store/state/selectors/insights/quietStreets';


// TODO: we need to handle this post launch
function fastTranslationHackForIL(s: string) {
  return s === 'Local History' ? 'תמחור ועסקאות' : s;
}

const makeHighlightMapFn = (group: HighlightGroup) => (item: IInsight) => ({
  title: fastTranslationHackForIL(item.preview),
  text: item.summary,
  type: item.category,
  previewText: item.tradeoff ? item.tradeoff.value : fastTranslationHackForIL(item.preview),
  group,
  subType: item.type,
  id: item.id,
  insightRecordId: item.insightRecordId,
});

const pricesCurrentIdsSelector = createSelector([
  pricesIdsSelector,
  activeInsightIdSelector,
], (pricesInsights, activeInsightId) => pricesInsights
  ? Object.values(pricesInsights).filter(id => id === activeInsightId)
  : []);

const makeILCarouselInsightsByTypeIdsSelector = (insightType: InsightType) => createSelector([
  makeInsightsByTypeSelector(InsightDescriptor.AnalystInsight),
  makeInsightsByTypeSelector(InsightDescriptor.DangerousIntersections),
], (analystInsights = [], dangerousIntersectionsInsights = []) => (
  [ ...analystInsights, ...dangerousIntersectionsInsights ]
    .filter(({ category }) => category === insightType)
    .map(({ id }) => id)
));

const otherInsightsSelector = createSelector([
  tradeoffIdsSelector,
  areaHighlightsIdsSelector,
  transportationIdsSelector,
  planningIdsSelector,
  nuisancesIdsSelector,
  insightsByIdsSelector,
  pricesCurrentIdsSelector,
  quietnessValueSelector,
  activeInsightIdSelector,
  impactfulTradeoffsSelector,
], (
  tradeoffIds,
  areaHighlightsIds,
  transportationIds,
  planningIds,
  nuisancesIds,
  insightsByIds,
  pricesIds,
  quietnessValue,
  defaultId,
  nonVerified
) => ([
  tradeoffIds,
  areaHighlightsIds,
  transportationIds,
  planningIds,
  nuisancesIds,
  insightsByIds,
  pricesIds,
  quietnessValue,
  defaultId,
  nonVerified,
] as const));

const pricesAnalystInsights = makeILCarouselInsightsByTypeIdsSelector(InsightType.Prices);
const educationAnalystInsights = makeILCarouselInsightsByTypeIdsSelector(InsightType.Education);
const livabilityAnalystInsights = makeILCarouselInsightsByTypeIdsSelector(InsightType.Livability);
const safetyAnalystInsights = makeILCarouselInsightsByTypeIdsSelector(InsightType.Safety);

const insightsSelector = createSelector([
  otherInsightsSelector,
  activeCollectionSelector,
  pricesAnalystInsights,
  educationAnalystInsights,
  livabilityAnalystInsights,
  safetyAnalystInsights,
], ([
    tradeoffIds,
    areaHighlightsIds,
    transportationIds,
    planningIds,
    nuisancesIds,
    insightsByIds,
    pricesIds,
    quietnessValue,
    defaultId,
    nonVerified,
  ],
  activeCollection,
  pricesAnalystInsightsIds,
  schoolsAnalystInsightsIds,
  livabilityAnalystInsightsIds,
  safetyAnalystInsightsIds
) => {
  let ids: string[] = [];
  switch (activeCollection) {
    case ExpandCollection.Tradeoff:
      ids = tradeoffIds;
      break;
    case ExpandCollection.Transportation:
      ids = transportationIds;
      break;
    case ExpandCollection.Nuisances:
      ids = nuisancesIds;
      break;
    case ExpandCollection.Planning:
      ids = planningIds;
      break;
    case ExpandCollection.Prices:
      ids = pricesIds;
      break;
    case ExpandCollection.SchoolsCarousel:
      ids = schoolsAnalystInsightsIds;
      break;
    case ExpandCollection.LivabilityCarousel:
      ids = livabilityAnalystInsightsIds;
      break;
    case ExpandCollection.SafetyCarousel:
      ids = safetyAnalystInsightsIds;
      break;
    case ExpandCollection.PricesCarousel:
      ids = pricesAnalystInsightsIds;
      break;
    case ExpandCollection.AreaHighlights:
      ids = areaHighlightsIds;
      break;
    case ExpandCollection.QuietStreets:
      ids = [ quietnessValue.id ];
      break;
    case ExpandCollection.Neighbourhood:
      /** @todo if insight is not for insights api */
      break;
    case ExpandCollection.Verified:
      ids = [ defaultId ];
      break;
    case ExpandCollection.NonVerified:
      ids = nonVerified.map(({ id }) => id);
      break;
  }

  return ids.map((id) => insightsByIds[id]);
});

interface ExpandedInsightsSummaryProps {
  collection: ExpandCollection;
  insights: IInsight[];
  id?: string;
  address?: string;
  newDesign?: boolean;
}

/** @todo think of a better way of doing these */
const BaseExpandedInsightsSummary: React.FC<ExpandedInsightsSummaryProps> = (props) => {
  const { collection, insights, newDesign } = props;

  const highlights = insights.map((insight) => {

    let group: HighlightGroup;

    switch (collection) {
      case ExpandCollection.Schools: {
        group = HighlightGroup.Schools;
        break;
      }
      case ExpandCollection.Prices: {
        group = HighlightGroup.Prices;
        break;
      }
      case ExpandCollection.PricesCarousel: {
        group = HighlightGroup.PricesCarousel;
        break;
      }
      case ExpandCollection.SchoolsCarousel: {
        group = HighlightGroup.SchoolsCarousel;
        break;
      }
      case ExpandCollection.LivabilityCarousel: {
        group = HighlightGroup.LivabilityCarousel;
        break;
      }
      case ExpandCollection.SafetyCarousel: {
        group = HighlightGroup.SafetyCarousel;
        break;
      }
      case ExpandCollection.NonVerified: {
        if (insight.type === InsightDescriptor.BuildingPermitRequests) {
          group = HighlightGroup.BuildingPermit;
        }
        break;
      }
      case ExpandCollection.Verified: {
        break;
      }
      case ExpandCollection.Tradeoff: {
        if (insight.tradeoff && insight.tradeoff.goodTradeoff) {
          group = HighlightGroup.ThingsYouWouldLove;
        }
        else if (insight.tradeoff && (!insight.tradeoff.goodTradeoff || insight.areaInsight)) {
          group = HighlightGroup.GoodToKnow;
        }
        break;
      }
      default: {
        group = HighlightGroup.ThingsYouShouldKnow;
      }
    }

    return makeHighlightMapFn(group)(insight);
  });

  return (
    <ExpandedHighlightsPage newDesign={newDesign} highlights={highlights} id={props.id} />
  );
};
// ^^^^^
const mapStateToProps = (state: State) => ({
  insights: insightsSelector(state),
  collection: activeCollectionSelector(state),
});

export const ExpandedInsightsSummary = connect(mapStateToProps)(BaseExpandedInsightsSummary);
